import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getSupabaseClient } from '../../supabaseClient';
import { useBusinessType } from '../../context/BusinessContext';
import {
  ArrowLeft,
  Chat,
  CircleNotch,
  WarningCircle,
  PaperPlaneTilt,
  AndroidLogo,
  User,
  Copy
} from '@phosphor-icons/react';
import { Card, CardContent } from '../../components/ui/card';
import PageHeader from '../../components/PageHeader';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const pageVariants = {
  initial: { opacity: 0 },
  animate: { 
    opacity: 1,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1]
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};

const messageVariants = {
  initial: { opacity: 0, y: 10 },
  animate: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.3,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};

const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';

  const cleaned = phoneNumber.replace(/\D/g, '');

  if (cleaned.length === 10 || (cleaned.length === 11 && cleaned.startsWith('1'))) {
    const number = cleaned.length === 11 ? cleaned.slice(1) : cleaned;
    return `(${number.slice(0,3)}) ${number.slice(3,6)}-${number.slice(6)}`;
  }

  if (cleaned.length > 10) {
    const countryCode = cleaned.startsWith('1') ? cleaned.slice(1,3) : cleaned.slice(0,2);
    const remaining = cleaned.startsWith('1') ? cleaned.slice(3) : cleaned.slice(2);
    return `+${countryCode} (${remaining.slice(0,3)}) ${remaining.slice(3,6)}-${remaining.slice(6)}`;
  }

  return phoneNumber;
};

const formatDate = (date) => {
  if (!date) return '';

  const d = new Date(date);
  return d.toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

const formatMessageTime = (date) => {
  if (!date) return '';

  const d = new Date(date);
  return d.toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

const getConversationDisplayName = (conversation) => {
  if (conversation?.contact?.name) {
    const name = conversation.contact.name;

    if (name.toLowerCase().includes('customer')) {
      const start = name.indexOf('(');
      const end = name.lastIndexOf(')');

      if (start !== -1 && end !== -1 && end > start) {
        return name.substring(start + 1, end).trim();
      }

      return formatPhoneNumber(conversation.contact.phone_number || conversation.from_number || '');
    }

    return name;
  }

  return formatPhoneNumber(conversation.from_number || conversation.contact?.phone_number || '');
};

// Helper function to determine if we should show contact name or phone number
const shouldShowContactName = (conversation) => {
  return conversation?.contact?.name && !conversation.contact.name.toLowerCase().includes('customer');
};

const getDisplayName = (conversation) => {
  if (shouldShowContactName(conversation)) {
    return conversation.contact.name;
  }
  return formatPhoneNumber(conversation.contact?.phone_number || conversation.from_number || '');
};

const TextDetailsPage = ({ id: propId, hideBackButton = false }) => {
  const { id: paramId } = useParams();
  const id = propId || paramId;
  const navigate = useNavigate();
  const { businessType } = useBusinessType();
  const supabase = getSupabaseClient(businessType);
  const messagesEndRef = useRef(null);
  const messageContainerRef = useRef(null);

  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [configMessages, setConfigMessages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [businessPhoneNumber, setBusinessPhoneNumber] = useState('');
  const [isAIConversation, setIsAIConversation] = useState(false);

  useEffect(() => {
    if (id) {
      fetchConversationDetails();
      fetchBusinessPhoneNumber();
    }
  }, [id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (messages.length > 0 && !loading) {
      scrollToBottom('auto');
      setTimeout(() => {
        scrollToBottom('auto');
      }, 500);
    }
  }, [loading]);

  const scrollToBottom = (behavior = 'smooth') => {
    if (messagesEndRef.current) {
      try {
        messagesEndRef.current.scrollIntoView({
          behavior: behavior,
          block: 'end',
        });
      } catch (error) {
        console.error('Error scrolling to bottom:', error);
        if (messageContainerRef.current) {
          messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
        }
      }
    } else if (messageContainerRef.current) {
      messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
    }
  };

  const fetchBusinessPhoneNumber = async () => {
    try {
      const { getUserAccountId } = await import('../../utils/accountUtils');
      const { accountId, error: accountIdError } = await getUserAccountId();
      if (accountIdError) throw accountIdError;
      if (!accountId) return;

      const { data, error } = await supabase
        .from('vapi_call_settings')
        .select('phone_number')
        .eq('account_id', accountId)
        .maybeSingle();

      if (error) throw error;

      if (data && data.phone_number) {
        setBusinessPhoneNumber(data.phone_number);
      }
    } catch (err) {
      console.error('Error fetching business phone number:', err);
    }
  };

  const fetchConversationDetails = async () => {
    // Guard clause: Don't fetch if ID is undefined
    if (!id) {
      setError('No conversation ID provided');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { getUserAccountId } = await import('../../utils/accountUtils');
      const { accountId, error: accountIdError } = await getUserAccountId();
      if (accountIdError) throw accountIdError;

      if (!accountId) throw new Error('Not authenticated');

      const { data: userLocations, error: locationsError } = await supabase
        .from('vapi_location_settings')
        .select('id')
        .eq('account_id', accountId);

      if (locationsError) throw locationsError;

      const locationIds = userLocations?.map(loc => loc.id) || [];

      const { data: thread, error: threadError } = await supabase
        .from('conversation_threads')
        .select(`
          id,
          created_at,
          updated_at,
          contact_id,
          location_id,
          node,
          node_config,
          config,
          summary,
          contact:contacts(*)
        `)
        .eq('id', id)
        .in('location_id', locationIds)
        .maybeSingle();

      if (threadError) throw threadError;
      if (!thread) throw new Error('Conversation not found or access denied');

      setConversation(thread);

      // Auto-mark as viewed when opening conversation details
      if (!thread.viewed) {
        try {
          await supabase
            .from('conversation_threads')
            .update({ viewed: true })
            .eq('id', id);

          thread.viewed = true;
        } catch (error) {
          console.error('Error marking conversation as viewed:', error);
        }
      }

      const hasConfig = !!thread.config;
      setIsAIConversation(hasConfig);

      if (hasConfig) {
        try {
          const configData = Array.isArray(thread.config) ? thread.config : [];
          const filteredConfig = configData.filter(item => 
            (item.role === 'user' || item.role === 'assistant' || item.role === 'tool') &&
            item.role !== 'system'
          );
          setConfigMessages(filteredConfig);
        } catch (e) {
          console.error('Error parsing config:', e);
        }
      }

      const { data: textMessages, error: messagesError } = await supabase
        .from('texts')
        .select('*')
        .eq('thread_id', id)
        .order('created_at', { ascending: true });

      if (messagesError) throw messagesError;

      setMessages(textMessages || []);

      setTimeout(() => {
        scrollToBottom();
      }, 300);

    } catch (err) {
      console.error('Error fetching conversation details:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sendingMessage || !conversation) return;

    try {
      setSendingMessage(true);
      const { getUserAccountId } = await import('../../utils/accountUtils');
      const { accountId, error: accountIdError } = await getUserAccountId();
      if (accountIdError) throw accountIdError;

      if (!accountId) throw new Error('Not authenticated');

      if (!businessPhoneNumber || !conversation.contact?.phone_number) {
        throw new Error('Missing phone number information');
      }

      const optimisticMessage = {
        id: `temp-${Date.now()}`,
        account_id: accountId,
        content: newMessage,
        created_at: new Date().toISOString(),
        from_number: businessPhoneNumber,
        to_number: conversation.contact.phone_number,
        thread_id: conversation.id,
        is_sending: true
      };

      setMessages(prev => [...prev, optimisticMessage]);
      setNewMessage('');

      const API_URL = process.env.REACT_APP_API_URL || 'https://cleaner-automation-936dbbf97f9e.herokuapp.com';

      const response = await fetch(`${API_URL}/api/convo/sms/manual`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: optimisticMessage.content,
          thread_id: conversation.id
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || `Failed to send message: ${response.status}`);
      }

      setMessages(prev => 
        prev.map(msg => msg.id === optimisticMessage.id ? {
          ...optimisticMessage,
          id: data.messageId || optimisticMessage.id,
          is_sending: false
        } : msg)
      );

      await supabase
        .from('conversation_threads')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', conversation.id);

    } catch (err) {
      console.error('Error sending message:', err);
      setMessages(prev => prev.filter(msg => !msg.is_sending));
      setSendError(`Failed to send message: ${err.message}`);
      setTimeout(() => setSendError(null), 5000);
    } finally {
      setSendingMessage(false);
    }
  };

  const isOwnMessage = (message) => {
    if (message.role) {
      return message.role === 'assistant' || message.role === 'tool';
    }

    return message.from_number === businessPhoneNumber;
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  const getCleanPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    // Remove all non-digit characters
    const cleaned = phoneNumber.replace(/\D/g, '');
    // If it starts with 1 and has 11 digits, remove the 1
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return cleaned.slice(1);
    }
    // If it's already 10 digits, return as is
    if (cleaned.length === 10) {
      return cleaned;
    }
    // Otherwise return the original
    return phoneNumber;
  };

  const renderConfigMessages = () => {
    if (!configMessages || configMessages.length === 0) return null;

    return (
      <div className="space-y-4">
        <AnimatePresence>
          {configMessages.map((message, index) => {
            if (!message.content && !message.tool_calls && message.role !== 'tool') return null;

            if (message.role === 'assistant' && message.tool_calls && message.tool_calls.length > 0) {
              return (
                <motion.div
                  key={`tool-call-${index}`}
                  variants={messageVariants}
                  initial="initial"
                  animate="animate"
                  className="flex flex-col ml-auto max-w-[85%]"
                >
                  <div className="flex items-center ml-auto mb-1">
                    <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center">
                      <Chat className="w-3 h-3" />
                    </div>
                    <span className="text-xs text-gray-500 ml-2">AI Assistant</span>
                  </div>

                  <div className="rounded-lg px-4 py-2.5 bg-amber-100 text-amber-800 border border-amber-200 ml-auto">
                    {message.tool_calls.map((toolCall, tcIndex) => (
                      <div key={`tc-${tcIndex}`} className="mb-2">
                        <div className="flex items-center">
                          <span className="text-sm font-semibold">{toolCall.function?.name}</span>
                        </div>
                        <div className="text-xs bg-amber-50 p-2 rounded mt-1 font-mono overflow-x-auto">
                          {toolCall.function?.arguments}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="text-xs text-gray-400 mt-1 text-right">
                    {formatMessageTime(new Date().toISOString())}
                  </div>
                </motion.div>
              );
            }

            if (message.role === 'tool') {
              return (
                <motion.div
                  key={`tool-response-${index}`}
                  variants={messageVariants}
                  initial="initial"
                  animate="animate"
                  className="flex flex-col ml-auto max-w-[85%]"
                >
                  <div className="flex items-center ml-auto mb-1">
                    <div className="w-6 h-6 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center">
                      <span className="text-xs font-medium">T</span>
                    </div>
                    <span className="text-xs text-gray-500 ml-2">Tool Response</span>
                  </div>

                  <div className="rounded-lg px-4 py-2.5 bg-gray-100 text-gray-800 border border-gray-200 ml-auto">
                    <div className="flex items-center">
                      <span className="text-sm font-semibold">{message.name}</span>
                    </div>
                    <div className="text-xs bg-gray-50 p-2 rounded mt-1 font-mono overflow-x-auto">
                      {message.content}
                    </div>
                  </div>

                  <div className="text-xs text-gray-400 mt-1 text-right">
                    {formatMessageTime(new Date().toISOString())}
                  </div>
                </motion.div>
              );
            }

            const isUser = message.role === 'user';

            return (
              <motion.div
                key={`message-${index}`}
                variants={messageVariants}
                initial="initial"
                animate="animate"
                className={`flex flex-col ${isUser ? 'mr-auto' : 'ml-auto'} max-w-[85%]`}
              >
                <div className={`flex items-center mb-1 ${isUser ? 'mr-auto' : 'ml-auto'}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                    isUser ? 'bg-gray-100 text-gray-600' : 'bg-blue-100 text-blue-600'
                  }`}>
                    {isUser ? (
                      <User className="w-3 h-3" />
                    ) : (
                      <span className="text-xs font-medium">B</span>
                    )}
                  </div>
                  <span className="text-xs text-gray-500 ml-2">
                    {isUser ? (conversation?.contact?.name || 'Customer') : 'Bella'}
                  </span>
                </div>

                <div className={`rounded-lg px-4 py-2.5 ${
                  isUser 
                    ? 'bg-gray-100 text-gray-800 border border-gray-200' 
                    : 'bg-blue-500 text-white'
                }`}>
                  <p className="whitespace-pre-wrap text-sm">{message.content}</p>
                </div>

                <div className={`text-xs text-gray-400 mt-1 ${isUser ? 'text-left' : 'text-right'}`}>
                  {formatMessageTime(new Date().toISOString())}
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    );
  };

  if (loading) {
    return (
      <div className={hideBackButton ? "h-full flex flex-col" : "h-screen bg-gray-50 flex flex-col md:h-screen"}>
        {!hideBackButton && (
          <div className="hidden md:block">
            <PageHeader
              title="Conversation"
              icon={() => (
                <motion.button
                  onClick={() => navigate(-1)}
                  className="flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors"
                  whileHover={{ x: -3 }}
                  transition={{ duration: 0.3 }}
                >
                  <ArrowLeft className="w-5 h-5" />
                </motion.button>
              )}
            />
          </div>
        )}
        <div className={hideBackButton ? "h-full flex flex-col" : "flex-1 flex flex-col px-0 md:px-4 lg:px-6"}>
          <div className="flex-1 flex flex-col min-h-0">
            <Card className="overflow-hidden shadow-none md:shadow-xl border-0 md:border md:border-gray-100 rounded-none md:rounded-2xl bg-white h-full flex flex-col">
              <CardContent className="p-0 h-full flex flex-col">
                <div className="p-5 sm:p-6 border-b border-gray-100 bg-gray-50/50 flex-shrink-0">
                  <div className="flex items-start justify-between mb-4">
                    {!hideBackButton && (
                      <div className="md:hidden">
                        <div className="w-6 h-6 bg-gray-200 rounded animate-pulse"></div>
                      </div>
                    )}
                    <div className="text-right">
                      <div className="h-3 w-20 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
                    <div className="h-5 w-32 bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-4 w-4 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                </div>

                {/* Skeleton Messages Area */}
                <div className="flex-1 min-h-0 flex flex-col">
                  <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                    {/* Skeleton Messages */}
                    {Array.from({ length: 6 }).map((_, index) => {
                      const isOwn = index % 3 === 0; // Every third message is from user
    return (
                        <div
                          key={`skeleton-message-${index}`}
                          className={`flex flex-col ${isOwn ? 'ml-auto' : 'mr-auto'} max-w-[85%]`}
                        >
                          <div className={`flex items-center mb-1 ${isOwn ? 'ml-auto' : 'mr-auto'}`}>
                            <div className="w-6 h-6 bg-gray-200 rounded-full animate-pulse"></div>
                            <div className="ml-2 h-3 w-12 bg-gray-200 rounded animate-pulse"></div>
                          </div>

                          <div className={`rounded-lg px-4 py-2.5 ${isOwn ? 'ml-auto' : 'mr-auto'}`}>
                            <div className="space-y-1">
                              <div className={`h-3 bg-gray-200 rounded animate-pulse ${
                                index % 4 === 0 ? 'w-32' : index % 4 === 1 ? 'w-48' : index % 4 === 2 ? 'w-24' : 'w-40'
                              }`}></div>
                              {index % 3 !== 2 && (
                                <div className={`h-3 bg-gray-200 rounded animate-pulse ${
                                  index % 2 === 0 ? 'w-28' : 'w-36'
                                }`}></div>
                              )}
                            </div>
                          </div>

                          <div className={`mt-1 ${isOwn ? 'text-right' : 'text-left'}`}>
                            <div className="h-2 w-8 bg-gray-200 rounded animate-pulse"></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Skeleton Message Input */}
                  <div className="border-t border-gray-200 p-4 bg-gray-50/50">
                    <div className="flex space-x-2">
                      <div className="flex-1 h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                      <div className="w-10 h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="text-center max-w-md px-6"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-50 mb-6">
            <WarningCircle className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-xl font-medium text-slate-900 mb-2">Unable to Load Conversation</h3>
          <p className="text-slate-600 mb-6">{error}</p>
          <motion.button
            onClick={() => navigate(-1)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-black rounded-md hover:bg-slate-800 transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Return to Messages
          </motion.button>
        </motion.div>
      </div>
    );
  }

  return (
    <motion.div
      className={hideBackButton ? "h-full flex flex-col" : "h-screen bg-gray-50 flex flex-col md:h-screen"}
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {!hideBackButton && (
        <div className="hidden md:block">
          <PageHeader
            title="Conversation"
            icon={() => (
              <motion.button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors"
                whileHover={{ x: -3 }}
                transition={{ duration: 0.3 }}
              >
                <ArrowLeft className="w-5 h-5" />
              </motion.button>
            )}
          />
        </div>
      )}

      {/* Mobile-only Header - Fixed at top of viewport */}
      {conversation && (
        <motion.div 
          variants={messageVariants}
          className="md:hidden fixed top-0 left-0 right-0 z-40 border-b border-gray-200 bg-white/95 backdrop-blur-sm"
          style={{
            paddingTop: 'env(safe-area-inset-top)'
          }}
        >
          <div className="p-4 sm:p-5">
            {/* First Row: Back Arrow + Name/Title + Copy Button + Date */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {!hideBackButton && (
                  <motion.button
                    onClick={() => navigate(-1)}
                    className="flex items-center justify-center w-6 h-6 text-gray-600 hover:text-gray-900 transition-colors flex-shrink-0"
                    whileHover={{ x: -2 }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </motion.button>
                )}
                <span className="text-lg font-medium text-gray-900 truncate">
                  {getDisplayName(conversation)}
                </span>
                {!shouldShowContactName(conversation) && conversation?.contact?.phone_number && (
                  <motion.button
                    onClick={() => copyToClipboard(getCleanPhoneNumber(conversation.contact.phone_number))}
                    className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    title="Copy phone number"
                  >
                    <Copy className="w-4 h-4" />
                  </motion.button>
                )}
              </div>
              <div className="text-right text-xs text-gray-500 flex-shrink-0">
                <div className="font-medium">{formatDate(conversation?.updated_at || conversation?.created_at)}</div>
              </div>
            </div>

            {/* Second Row: Message Icon + Phone Number (only if showing contact name) */}
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center">
                                      <Chat className="w-4 h-4 text-green-600" />
              </div>
              {shouldShowContactName(conversation) && conversation?.contact?.phone_number && (
                <>
                  <span className="text-sm text-gray-500 font-normal">
                    {formatPhoneNumber(conversation.contact.phone_number)}
                  </span>
                  <motion.button
                    onClick={() => copyToClipboard(getCleanPhoneNumber(conversation.contact.phone_number))}
                    className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    title="Copy phone number"
                  >
                    <Copy className="w-4 h-4" />
                  </motion.button>
                </>
              )}
            </div>
          </div>
        </motion.div>
      )}

      <div className={hideBackButton ? "h-full flex flex-col" : "flex-1 flex flex-col px-0 md:px-4 lg:px-6"}>
        <div className={`flex-1 flex flex-col min-h-0 ${conversation ? 'md:mt-0' : ''}`}>
          <Card className={`overflow-hidden shadow-none md:shadow-xl border-0 md:border md:border-gray-100 rounded-none md:rounded-2xl bg-white h-full flex flex-col ${conversation ? 'md:pt-0 pt-[calc(env(safe-area-inset-top)+8rem)]' : ''}`}>
            <CardContent className="p-0 h-full flex flex-col">
              {/* Header - Desktop only, hidden on mobile */}
              <div className="hidden md:block p-5 sm:p-6 border-b border-gray-200 bg-gray-50/50 flex-shrink-0">
                <div className="flex items-start justify-between mb-4">
                  {!hideBackButton && (
                    <motion.button
                      onClick={() => navigate(-1)}
                      className="md:hidden flex items-center justify-center w-6 h-6 text-gray-600 hover:text-gray-900 transition-colors"
                      whileHover={{ x: -2 }}
                      whileTap={{ scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ArrowLeft className="w-5 h-5" />
                    </motion.button>
                  )}
                  <div className={`text-right text-xs text-gray-500 ${!hideBackButton ? 'md:ml-0' : ''}`}>
                    <div className="font-medium">{formatDate(conversation?.updated_at || conversation?.created_at)}</div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center">
                      <Chat className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="text-lg font-medium text-gray-900">
                      {getDisplayName(conversation)}
                    </span>
                  </div>
                  {shouldShowContactName(conversation) && conversation?.contact?.phone_number && (
                    <div className="flex items-center gap-2 ml-10 sm:ml-0">
                      <span className="text-sm text-gray-500 font-normal">
                        {formatPhoneNumber(conversation.contact.phone_number)}
                      </span>
                      <motion.button
                        onClick={() => copyToClipboard(getCleanPhoneNumber(conversation.contact.phone_number))}
                        className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        title="Copy phone number"
                      >
                        <Copy className="w-4 h-4" />
                      </motion.button>
                    </div>
                  )}
                  {!shouldShowContactName(conversation) && conversation?.contact?.phone_number && (
                    <motion.button
                      onClick={() => copyToClipboard(getCleanPhoneNumber(conversation.contact.phone_number))}
                      className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors ml-10 sm:ml-0"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      title="Copy phone number"
                    >
                      <Copy className="w-4 h-4" />
                    </motion.button>
                  )}
                </div>
              </div>

              {/* Messages Area - Add bottom padding on mobile for fixed input */}
              <div className="flex-1 min-h-0 flex flex-col">
                <div 
                  ref={messageContainerRef}
                  className="flex-1 overflow-y-auto px-6 py-4 space-y-4 md:pb-4 pb-20"
                  style={{ 
                    scrollBehavior: 'smooth',
                    overscrollBehavior: 'contain'
                  }}
                >
                  <div className="min-h-full">
                    {(() => {
                      if (isAIConversation && configMessages) {
                        return configMessages.length === 0 ? (
                          <div className="flex flex-col items-center text-center py-8">
                            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                              <Chat className="w-8 h-8 text-gray-300" />
                            </div>
                            <h3 className="text-gray-500 text-lg font-medium">No messages yet</h3>
                            <p className="text-gray-400 text-sm mt-1">
                              This AI conversation has no messages
                            </p>
                          </div>
                        ) : renderConfigMessages();
                      } else if (messages.length === 0) {
                        return (
                          <div className="flex flex-col items-center text-center py-8">
                            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                              <Chat className="w-8 h-8 text-gray-300" />
                            </div>
                            <h3 className="text-gray-500 text-lg font-medium">No messages yet</h3>
                            <p className="text-gray-400 text-sm mt-1">
                              Send a message to start the conversation
                            </p>
                          </div>
                        );
                      } else {
                        return (
                          <>
                            {messages.map((message, index) => {
                              const isOwn = isOwnMessage(message);

                              return (
                                <motion.div
                                  key={message.id}
                                  className={`flex flex-col ${isOwn ? 'ml-auto' : 'mr-auto'} max-w-[85%]`}
                                  variants={messageVariants}
                                  initial="initial"
                                  animate="animate"
                                  custom={index}
                                >
                                  <div className={`flex items-center mb-1 ${isOwn ? 'ml-auto' : 'mr-auto'}`}>
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                      isOwn ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                                    }`}>
                                      {isOwn ? (
                                        <span className="text-xs font-medium">B</span>
                                      ) : (
                                        <User className="w-3 h-3" />
                                      )}
                                    </div>
                                    <span className="text-xs text-gray-500 ml-2">
                                      {isOwn ? 'Bella' : (conversation?.contact?.name || 'Customer')}
                                    </span>
                                  </div>

                                  <div className={`rounded-lg px-4 py-2.5 ${
                                    isOwn 
                                      ? 'bg-blue-500 text-white ml-auto' 
                                      : 'bg-gray-100 text-gray-800 mr-auto'
                                  } ${message.is_sending ? 'opacity-70' : ''}`}>
                                    <p className="whitespace-pre-wrap text-sm">{message.content}</p>
                                  </div>

                                  <div className={`text-xs text-gray-400 mt-1 ${isOwn ? 'text-right' : 'text-left'}`}>
                                    {formatMessageTime(message.created_at)}
                                    {message.is_sending && (
                                      <motion.span 
                                        className="ml-1 inline-flex"
                                        animate={{ rotate: 360 }}
                                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                      >
                                        <CircleNotch className="w-3 h-3" />
                                      </motion.span>
                                    )}
                                  </div>
                                </motion.div>
                              );
                            })}
                            <div ref={messagesEndRef} className="h-2" />
                          </>
                        );
                      }
                    })()}
                  </div>
                </div>

                {/* Message Input - Desktop only, hidden on mobile */}
                <div className="hidden md:block border-t border-gray-200 p-4 bg-gray-50/50">
                  <AnimatePresence>
                    {sendError && (
                      <motion.div 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="mb-2 p-2 bg-red-50 border border-red-100 rounded-md text-sm text-red-500"
                      >
                        {sendError}
                      </motion.div>
                    )}
                  </AnimatePresence>
                  <form onSubmit={handleSendMessage} className="flex space-x-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message..."
                      className="flex-1 bg-white"
                      disabled={sendingMessage}
                    />
                    <Button 
                      type="submit"
                      size="sm"
                      disabled={!newMessage.trim() || sendingMessage}
                      className="bg-black hover:bg-gray-800 text-white"
                    >
                      {sendingMessage ? (
                        <CircleNotch className="w-4 h-4 animate-spin" />
                      ) : (
                        <PaperPlaneTilt className="w-4 h-4" />
                      )}
                    </Button>
                  </form>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Mobile-only Message Input - Fixed at bottom of viewport */}
      <motion.div 
        variants={messageVariants}
        className="md:hidden fixed bottom-0 left-0 right-0 z-50 border-t border-gray-200 bg-white/95 backdrop-blur-sm p-4"
        style={{
          paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))'
        }}
      >
        <AnimatePresence>
          {sendError && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mb-2 p-2 bg-red-50 border border-red-100 rounded-md text-sm text-red-500"
            >
              {sendError}
            </motion.div>
          )}
        </AnimatePresence>
        <form onSubmit={handleSendMessage} className="flex space-x-2">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message..."
            className="flex-1 bg-white"
            disabled={sendingMessage}
          />
          <Button 
            type="submit"
            size="sm"
            disabled={!newMessage.trim() || sendingMessage}
            className="bg-black hover:bg-gray-800 text-white"
          >
            {sendingMessage ? (
              <CircleNotch className="w-4 h-4 animate-spin" />
            ) : (
              <PaperPlaneTilt className="w-4 h-4" />
            )}
          </Button>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default TextDetailsPage; 